import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Order, LogElement } from '@models/order';
import { Product, ProductQuery } from '@models/product';
import { ProductService } from '@services/product.service';
import { clearCopiedSuperbill } from '@classes/superbill';

export class DuplicateJob {
  static lookupCurrentProduct(
    productService: ProductService,
    productElement: any
  ): Observable<Product | null> {
    const product = productElement?.product;
    const id = String(product?._id || '').trim();
    if (id && id !== 'NEW' && id !== 'null') {
      return productService.getProduct(id).pipe(catchError(() => of(null)));
    }

    const vendorId = product?.vendor?._id || product?.vendor;
    const model = String(product?.model || '').trim();
    const color = String(product?.color || '').trim();
    const size = String(product?.size || '').trim();
    if (!vendorId || !model) {
      return of(null);
    }

    const query = new ProductQuery();
    query.model.value = model;
    query.model.selected = true;
    query.color.value = color;
    query.color.selected = !!color;
    query.size.value = size;
    query.size.selected = !!size;
    query.type.value = String(product?.type || '').trim();
    query.type.selected = !!query.type.value;

    return productService.queryVendorProductsFields(vendorId, query).pipe(
      map((res: any) => {
        if (Array.isArray(res)) {
          return res[0] || null;
        }
        return res || null;
      }),
      catchError(() => of(null))
    );
  }

  static isInStock(product: Product | null): boolean {
    const qty = Number(product?.quantity);
    return !!product && !isNaN(qty) && qty > 0;
  }

  static buildDuplicateOrder(
    original: any,
    originalInvoice: string,
    invoiceNumber: string,
    currentProduct: Product | null,
    inStock: boolean,
    context: {
      patientId: string;
      userId: string;
      store: any;
      initials: string;
    }
  ): Order {
    const clone = JSON.parse(JSON.stringify(original || {}));
    clearCopiedSuperbill(clone);
    clone._id = '';
    clone.invoiceNumber = invoiceNumber;
    clone.linkedInvoice = originalInvoice;
    clone.date = new Date();
    clone.dateAdded = undefined;
    clone.dateUpdated = undefined;
    clone.vendorOrder = null;
    clone.rxStatus = '';
    clone.eta = '';
    clone.archived = false;
    clone.framesRemoved = [];
    clone.tax = 0;
    clone.shipping = 0;
    clone.total = 0;
    clone.frameOnlyTaxCredit = 0;
    clone.fulfillment = 'PICKUP';
    clone.patient = context.patientId;
    clone.user = context.userId;
    clone.store = context.store;
    delete clone.initials;

    (clone.products || []).forEach((productElement: any) => {
      productElement._id = null;
      productElement.tax = 0;
      productElement.shipping = 0;
      productElement.destination = inStock ? '' : 'SEND TO STACK';

      if (currentProduct) {
        const nextProduct = JSON.parse(JSON.stringify(currentProduct));
        nextProduct.vendor = nextProduct.vendor || productElement.product?.vendor;
        productElement.product = nextProduct;
        productElement.cost = currentProduct.cost ?? 0;
        productElement.pricingFormula = currentProduct.pricingFormula || '';
        const calculated = Product.calculateProductPrice({
          cost: currentProduct.cost,
          pricingFormula: currentProduct.pricingFormula,
        });
        productElement.retail =
          calculated != null ? calculated : currentProduct.retail ?? 0;
        if (currentProduct.type) {
          productElement.type = currentProduct.type;
        }
      }

      const retail = Number(productElement.retail) || 0;
      const custom = Number(productElement.custom) || 0;
      const lensPrice = Number(productElement.lens?.price) || 0;
      productElement.subtotal = retail + custom + lensPrice;

      if (!productElement.frame) {
        productElement.frame = {};
      }
      productElement.frame.pof = false;
      productElement.frame.deposit = 0;
      productElement.frame.activity = [];
      productElement.frame.activityStatus = '';
      productElement.frame.dateReceived = '';
      productElement.frame.followUpDate = '';
      productElement.frame.followUpStatus = '';
      productElement.frame.referenceNumber = '';
      productElement.frame.labVendor = '';
      productElement.frame.carrier = '';
      productElement.frame.trackingNumber = '';
      productElement.frame.shipDate = '';
      productElement.frame.repairItems = [];
      productElement.frame.orderDate = '';
      productElement.frame.failedInspection = false;

      if (productElement.lens) {
        productElement.lens.labReferenceNumber = '';
        productElement.lens.POLLabReferenceNumber = '';
        productElement.lens.date = '';
        productElement.lens.failedInspection = '';
      }

      if (inStock) {
        productElement.frame.physicalStatus = 'ENCLOSED';
        productElement.frame.preOrderStatus = 'ENCLOSED';
        productElement.frame.enclosed = true;
      } else {
        productElement.frame.physicalStatus = '';
        productElement.frame.preOrderStatus = '';
        productElement.frame.enclosed = false;
      }
    });

    if (inStock) {
      clone.status = '';
      clone.todo = 'SAVE';
      clone.jobAction = '';
      clone.jobActions = [];
      clone.lensStatus = '';
    } else {
      clone.status = 'TO ORDER';
      clone.todo = 'ORDER';
      clone.jobAction = 'SEND TO STACK';
      clone.jobActions = ['SEND TO STACK'];
      clone.lensStatus = '';
      const frame = clone.products?.[0]?.frame;
      if (frame) {
        frame.activity = [
          {
            date: new Date(),
            status: 'TO ORDER',
            referenceNumber: '',
            carrier: '',
            trackingNumber: '',
            labVendor: '',
          },
        ];
        frame.activityStatus = 'TO ORDER';
      }
    }

    const created = new LogElement();
    created.initials = context.initials;
    created.user = context.userId;
    created.item = 'JOB CREATED';
    created.notes = '';
    created.date = Date.now().toString();

    const duplicateLog = new LogElement();
    duplicateLog.initials = context.initials;
    duplicateLog.user = context.userId;
    duplicateLog.item = originalInvoice
      ? `DUPLICATE FROM ${originalInvoice}`
      : 'DUPLICATE';
    duplicateLog.notes = inStock
      ? 'FRAME AVAILABLE IN INVENTORY; CURRENT PRICING APPLIED'
      : 'FRAME OUT OF STOCK; SENT TO TO ORDER WITH CURRENT PRICING';
    duplicateLog.date = Date.now().toString();
    clone.log = [created, duplicateLog];

    return clone as Order;
  }
}
